import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { updatePassword } from '@/lib/supabase/auth';

const passwordPolicyMessage =
  'Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character';
const passwordPolicy = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

const updatePasswordSchema = z.object({
  password: z
    .string()
    .min(8, passwordPolicyMessage)
    .regex(passwordPolicy, passwordPolicyMessage),
  confirmPassword: z.string().min(8, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type UpdatePasswordFormValues = z.infer<typeof updatePasswordSchema>;

export default function UpdatePasswordPage() {
  const [status, setStatus] = useState<'checking' | 'ready' | 'success' | 'invalid'>('checking');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const form = useForm<UpdatePasswordFormValues>({
    resolver: zodResolver(updatePasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    const checkRecoverySession = async () => {
      try {
        // Let Supabase parse hash tokens if present in URL.
        await new Promise((resolve) => setTimeout(resolve, 500));

        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          setErrorMessage(error.message);
          setStatus('invalid');
          return;
        }

        if (session?.user) {
          setStatus('ready');
          return;
        }

        setErrorMessage('This password reset link is invalid or has expired.');
        setStatus('invalid');
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'Failed to verify reset link');
        setStatus('invalid');
      }
    };

    checkRecoverySession();
  }, []);

  const onSubmit = async (values: UpdatePasswordFormValues) => {
    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      await updatePassword(values.password);
      setStatus('success');
      setTimeout(() => navigate('/signin', { replace: true }), 1500);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to update password');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (status === 'checking') {
    return (
      <div className="container flex h-screen w-screen flex-col items-center justify-center px-4">
        <div className="mx-auto flex w-full max-w-sm flex-col justify-center space-y-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center space-y-4 py-6 text-center">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Validating reset link...</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="container flex h-screen w-screen flex-col items-center justify-center px-4">
        <div className="mx-auto flex w-full max-w-sm flex-col justify-center space-y-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center space-y-4 py-6 text-center">
                <CheckCircle className="h-10 w-10 text-green-500" />
                <p className="text-sm text-muted-foreground">
                  Password updated successfully. Redirecting to sign in...
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="container flex h-screen w-screen flex-col items-center justify-center px-4">
        <div className="mx-auto flex w-full max-w-sm flex-col justify-center space-y-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center space-y-4 py-6 text-center">
                <AlertCircle className="h-10 w-10 text-destructive" />
                <p className="text-sm text-muted-foreground">
                  {errorMessage || 'Reset link is invalid or expired.'}
                </p>
                <Button onClick={() => navigate('/auth/forgot-password')}>
                  Request a new reset link
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container flex h-screen w-screen flex-col items-center justify-center px-4">
      <div className="mx-auto flex w-full max-w-sm flex-col justify-center space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Set a new password</CardTitle>
            <CardDescription>Enter your new password to complete recovery.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {errorMessage && (
              <Alert variant="destructive">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New password</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="new-password" {...field} />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        Use at least 8 characters with uppercase, lowercase, a number, and a special character.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm password</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Update password
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
